<template>
    <div class="space-y-6">
        <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 transition-all duration-200 ease-in-out">
            <div class="md:grid md:grid-cols-3 md:gap-6" v-if="Object.keys(invoice).length === 0">
                <div class="md:col-span-1">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">Purchase</h3>
                    <p class="mt-1 text-sm text-gray-500">Finalise and complete the order</p>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <form class="space-y-6" method="POST" @submit.prevent>
                        <div v-if="Object.keys(state).length > 0">
                            <table class="table-fixed w-full divide-y divide-gray-200">
                                <tr v-if="state.customer?.saved">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        Customer
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-500">
                                        {{ state.customer.email }}
                                    </td>
                                </tr>
                                <tr v-if="state.product?.saved">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        Product
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-500">
                                        {{ state.product.price.product.name }}&nbsp;{{ state.product.price.recurring ? '(' + state.product.price.nickname + ')' : '' }} <template v-if="state.product.quantity > 1"> x {{ state.product.quantity }}</template>
                                    </td>
                                </tr>
                                <tr v-if="state.product?.coupon?.saved">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        Discount
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-500">
                                        {{ state.product.coupon.name }}
                                    </td>
                                </tr>
                                <template v-if="state.customer?.subscription?.cost && state.type.key !== 'single'">
                                    <tr>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            Current Cost
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            {{ currencyFormat(state.product?.price?.currency ?? 'gbp', existingCost ?? 0) }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            Changes
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            {{ currencyFormat(state.product?.price?.currency ?? 'gbp', transactionCost ?? 0) }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            Total Cost
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            {{ currencyFormat(state.product?.price?.currency ?? 'gbp', totalCost ?? 0) }}
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            Total Cost
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            {{ currencyFormat(state.product?.price?.currency ?? 'gbp', transactionCost ?? 0) }}
                                        </td>
                                    </tr>
                                </template>
                                <tr>
                                    <td class="px-6 py-4 text-sm font-medium text-gray-900" colspan="2">
                                        <p class="text-xs">Shown price is not VAT inclusive, and will not account for pro-ration unless otherwise stated. The invoice that is sent to the customer will include these things.</p>
                                    </td>
                                </tr>
                            </table>
                        </div>
                        <div>
                            <div class="flex justify-center mt-4">
                                <button type="submit" @click="validate(v$)" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <LockClosedIcon class="h-5 w-5 mr-2" v-if="!creatingOrder" />
                                    <ArrowPathIcon class="h-5 w-5 mr-2 animate-spin" v-if="creatingOrder" />
                                    Complete Order
                                </button>
                            </div>
                            <div class="text-sm mt-2 text-center">
                                <p v-for="error of v$.$errors" :key="error.$uid" class="text-red-700 text-sm mb-2 text-center">
                                    {{ error.$message }}
                                </p>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <div class="md:grid md:grid-cols-3 md:gap-6" v-if="Object.keys(invoice).length > 0">
                <div class="md:col-span-1 flex">
                    <CheckCircleIcon class="h-6 w-6 text-green-600 mr-4" />
                    <h3 class="text-lg font-medium leading-6 text-gray-900 flex-1">Purchase</h3>
                </div>
                <div class="md:col-span-2" v-if="Object.keys(invoice).length > 0 && state.saved">
                    <div class="flex justify-center">
                        <p class="text-sm text-green-600">Order completed, invoice payment email has been sent successfully.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import {Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions} from "@headlessui/vue"
import {CheckIcon, ChevronUpDownIcon} from "@heroicons/vue/24/solid"
import {inject, ref, toRef, watch} from "vue"
import {CheckCircleIcon, ChevronDownIcon, ArrowPathIcon, LockClosedIcon} from "@heroicons/vue/24/outline"
import useVuelidate from "@vuelidate/core"
import {helpers, sameAs} from "@vuelidate/validators"
import {useBus} from "../../../../app";

const rules = {
    customer: {
        saved: { isTrue: helpers.withMessage('Please save the customer before continuing', sameAs(true)) }
    },
    product: {
        saved: { isTrue: helpers.withMessage('Please save the product before continuing', sameAs(true))}
    }
}

export default {
    name: "Purchase",
    props: {
        purchase: {
            type: Object,
            required: true,
        }
    },
    components: {
        Listbox,
        ListboxButton,
        ListboxLabel,
        ListboxOption,
        ListboxOptions,
        CheckIcon,
        ChevronUpDownIcon,
        ChevronDownIcon,
        LockClosedIcon,
        CheckCircleIcon,
        ArrowPathIcon,
    },
    setup(props) {
        const alert = inject('alert')
        const bus = useBus()
        const creatingOrder = ref(false)
        const transactionCost = ref(null)
        const existingCost = ref(null)
        const totalCost = ref(null)

        const invoice = ref({})
        const state = toRef(props, 'purchase')

        const v$ = useVuelidate(rules, state, {$lazy: true})

        const calculateCost = () => {
            let productCost = state.value.product?.price?.unit_amount * (state.value.product?.quantity ?? 1) ?? 0
            let discountType =  state.value.product?.coupon?.amount_off ? 'amount' : 'percent'

            let estimatedCost = 0;
            if (Object.keys(state.value.product?.coupon ?? {}).length > 0) {
                estimatedCost = discountType === 'amount' ?
                    productCost - state.value.product?.coupon?.amount_off :
                    productCost - (productCost * (state.value.product?.coupon?.percent_off / 100));
            } else {
                estimatedCost = productCost
            }

            transactionCost.value = estimatedCost

            if (state.value.customer?.subscription?.cost && state.value.type.key !== 'single') {
                existingCost.value = 0
                let subscriptionItems = state.value.customer?.subscription.data.items.data

                subscriptionItems.forEach((item) => {
                    existingCost.value += item.price?.unit_amount * (item.quantity ?? 1) ?? 0
                })

                totalCost.value = transactionCost.value + existingCost.value
            }
        }

        watch(() => state.value, () => {
            calculateCost()
        }, {deep: true})

        const validate = async (v$) => {
            const isValid = await v$.$validate()

            if (!isValid) {
                return;
            }

            if (state.value.customer?.subscription?.data?.id) {
                alert.fire({
                    title: 'Customer will be charged immediately.',
                    text: 'The customer already has a subscription, and therefore payment details are already on file. Confirming this order will immediately charge the customer. Do you wish to proceed?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, complete order',
                    cancelButtonText: 'No, go back'
                })
                    .then((selected) => {
                        if (selected.value) {
                            proceed()
                        }
                    })
            } else {
                await proceed()
            }
        }

        const proceed = async () => {
            creatingOrder.value = true
            await axios.post(route('transactions.create'), {state: state.value})
                .then((response) => {
                    creatingOrder.value = false
                    if (response.status === 200) {
                        invoice.value = response.data
                        state.value.saved = true
                    }
                }).catch(error => {
                    creatingOrder.value = false
                    bus.$emit('alert.danger', error.response?.data?.message ?? 'Sale Failed')
                })
        }

        return {
            v$,
            rules,
            state,
            validate,
            proceed,
            invoice,
            creatingOrder,
            transactionCost,
            totalCost,
            existingCost,
        }
    },
}
</script>

<style scoped>

</style>
